import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Paper, Typography, Stack, Chip, Tooltip, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, InputAdornment, IconButton, Button, FormControl,
  InputLabel, Select, MenuItem, CircularProgress, Divider,
} from '@mui/material';
import {
  Search, Close, Add, DeleteOutline, ShoppingCart,
  Inventory2, Send, CheckCircle, Warning,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { consumablesUrl, saveRequisitions } from '../BaseURL';

const PRIMARY = '#41a386';
const PRIMARY_DARK = '#2f8068';
const BORDER = '#e5e9ef';
const MUTED  = '#64748b';
const STRONG = '#0f172a';
const LABEL_SX = {
  fontSize: '0.66rem', fontWeight: 700, color: '#94a3b8',
  textTransform: 'uppercase', letterSpacing: '0.6px',
};

/**
 * Consumables requisition dialog — opened by "in-charge" staff from
 * their dashboard. Mirrors the Pharmacy → drug Requisition UX:
 *   • left:  searchable catalogue of active consumables with on-hand stock
 *   • right: editable cart of selected lines with quantity + remarks
 *   • submit posts to /saveRequisitions with itemType='consumable'.
 *
 * The requests land in Procurement → Manage Requisitions alongside drugs;
 * each row carries a "Consumable" chip there. On approve, the backend
 * decrements consumables.stock_qty and writes a consumable_stock_log
 * 'Issue' entry.
 */
const ConsumableRequisitionDialog = ({ open, onClose, onSubmitted }) => {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [search, setSearch]       = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState([]);

  // Cart of selected lines. { consumableId, name, base_unit, pack_label,
  // pack_size, stock_qty, quantity, remarks }
  const [cart, setCart] = useState([]);
  const [quickQty, setQuickQty] = useState({});  // { consumableId: '12' } before adding

  const [submitting, setSubmitting] = useState(false);

  // Current user — used for requestedBy + requestedByUserId.
  const userInfo = useMemo(() => {
    try {
      const raw = localStorage.getItem('userInfo');
      if (raw) return JSON.parse(raw);
    } catch (_) {}
    return null;
  }, []);

  // Load catalogue when dialog opens.
  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(consumablesUrl, { params: { status: 'active' } });
      if (data?.success) {
        setItems(data.results || []);
        setCategories(data.categories || []);
      } else {
        setItems([]);
      }
    } catch (err) {
      console.error('[req-cons] catalogue load failed:', err.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadItems();
    } else {
      // Reset when closing so reopening starts clean.
      setCart([]);
      setQuickQty({});
      setSearch('');
      setCategoryFilter('all');
    }
  }, [open, loadItems]);

  // Filtered catalogue
  const filteredItems = useMemo(() => {
    return items.filter(i => {
      if (categoryFilter !== 'all' && i.category !== categoryFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        const blob = `${i.name} ${i.category || ''}`.toLowerCase();
        if (!blob.includes(s)) return false;
      }
      return true;
    });
  }, [items, search, categoryFilter]);

  const inCart = (id) => cart.some(c => c.consumableId === id);

  const addToCart = (item) => {
    const qty = parseInt(quickQty[item.id], 10) || 0;
    if (qty <= 0) {
      Swal.fire({ icon: 'warning', title: 'Enter a quantity', text: `How many ${item.base_unit}s of ${item.name}?` });
      return;
    }
    if (qty > item.stock_qty) {
      Swal.fire({
        icon: 'warning', title: 'Not enough in store',
        text: `Only ${item.stock_qty} ${item.base_unit}${item.stock_qty === 1 ? '' : 's'} of ${item.name} on hand.`,
      });
      return;
    }
    if (inCart(item.id)) {
      // Bump quantity on existing line instead of duplicating.
      setCart(prev => prev.map(c => c.consumableId === item.id ? { ...c, quantity: qty } : c));
    } else {
      setCart(prev => [...prev, {
        consumableId: item.id,
        name:         item.name,
        base_unit:    item.base_unit,
        pack_label:   item.pack_label,
        pack_size:    item.pack_size,
        stock_qty:    item.stock_qty,
        quantity:     qty,
        remarks:      '',
      }]);
    }
    setQuickQty(prev => ({ ...prev, [item.id]: '' }));
  };

  const updateLine = (id, key, value) => {
    setCart(prev => prev.map(c => c.consumableId === id ? { ...c, [key]: value } : c));
  };

  const removeLine = (id) => {
    setCart(prev => prev.filter(c => c.consumableId !== id));
  };

  const totalUnits = cart.reduce((s, c) => s + (parseInt(c.quantity, 10) || 0), 0);

  const handleSubmit = async () => {
    if (cart.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Empty cart', text: 'Add at least one consumable.' });
      return;
    }
    // Validate each line
    for (const c of cart) {
      const q = parseInt(c.quantity, 10);
      if (!Number.isFinite(q) || q <= 0) {
        Swal.fire({ icon: 'warning', title: 'Invalid quantity', text: `${c.name} has an invalid quantity.` });
        return;
      }
      if (q > c.stock_qty) {
        Swal.fire({
          icon: 'warning', title: 'Exceeds stock',
          text: `${c.name}: requested ${q}, but only ${c.stock_qty} on hand.`,
        });
        return;
      }
    }

    // Confirmation
    const itemsHtml = cart.map(c => `<tr>
      <td style="padding:4px 8px;text-align:left">${c.name}</td>
      <td style="padding:4px 8px;text-align:right">${c.quantity} ${c.base_unit}${c.quantity === 1 ? '' : 's'}</td>
    </tr>`).join('');
    const ok = await Swal.fire({
      title: 'Submit consumables request?',
      html: `<div style="text-align:left;font-size:0.88rem">
        <table style="width:100%;border-collapse:collapse;margin:6px 0">
          <thead><tr style="background:#f1f5f9;color:#475569">
            <th style="padding:6px 8px;text-align:left">Item</th>
            <th style="padding:6px 8px;text-align:right">Qty</th>
          </tr></thead>
          <tbody>${itemsHtml}</tbody>
          <tfoot><tr style="font-weight:700;color:#0f172a;border-top:2px solid #e2e8f0">
            <td style="padding:8px;text-align:right">Total lines</td>
            <td style="padding:8px;text-align:right">${cart.length}</td>
          </tr></tfoot>
        </table>
      </div>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: PRIMARY,
      cancelButtonColor: '#d32f2f',
      confirmButtonText: 'Yes, submit',
      width: 540,
    });
    if (!ok.isConfirmed) return;

    setSubmitting(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const requisitions = cart.map(c => ({
        // productCode holds the consumable.id so the approver knows exactly
        // which row to debit. The approve handler reads this back.
        productCode:       String(c.consumableId),
        productName:       c.name,
        requestedQuantity: parseInt(c.quantity, 10),
        quantityReceived:  0,
        currentBalance:    c.stock_qty,
        remarks:           c.remarks || `Unit: ${c.base_unit}`,
        requestedBy:       userInfo?.name || userInfo?.username || 'Unknown',
        requestedByUserId: userInfo?.id || null,
        approvedBy:        null,
        requestDate:       today,
        status:            'pending',
        itemType:          'consumable',
      }));

      const { data } = await axios.post(saveRequisitions, { requisitions });
      if (data?.success) {
        Swal.fire({
          icon: 'success', title: 'Request submitted',
          text: `${cart.length} consumable${cart.length === 1 ? '' : 's'} sent to Procurement for approval.`,
          timer: 2000, showConfirmButton: false,
        });
        if (typeof onSubmitted === 'function') onSubmitted();
        onClose();
      } else {
        Swal.fire({ icon: 'error', title: 'Failed', text: data?.message || 'Could not save request.' });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: err?.response?.data?.message || err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}
      PaperProps={{ sx: { borderRadius: 2, width: '95vw', height: '92vh', m: 0 } }}>
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', gap: 1.25,
        px: 2.5, py: 1.5,
        bgcolor: 'rgba(65,163,134,0.10)', color: PRIMARY,
        borderBottom: `1px solid rgba(65,163,134,0.30)`,
      }}>
        <ShoppingCart sx={{ fontSize: 22 }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>
            Consumables Request
          </Typography>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: STRONG, lineHeight: 1.2 }}>
            New request to Procurement
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={submitting}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, bgcolor: '#f8fafc', display: 'flex', overflow: 'hidden' }}>
        {/* LEFT — catalogue */}
        <Box sx={{
          width: 420, flexShrink: 0,
          borderRight: `1px solid ${BORDER}`,
          bgcolor: '#fff', display: 'flex', flexDirection: 'column',
        }}>
          <Box sx={{ p: 1.25, borderBottom: `1px solid ${BORDER}` }}>
            <TextField fullWidth size="small" placeholder="Search consumables…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: MUTED }} /></InputAdornment>,
              }} />
            <FormControl size="small" fullWidth sx={{ mt: 1 }}>
              <InputLabel>Category</InputLabel>
              <Select label="Category" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                <MenuItem value="all">All categories</MenuItem>
                {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress size={20} sx={{ color: PRIMARY }} />
              </Box>
            ) : filteredItems.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center', color: MUTED }}>
                <Inventory2 sx={{ fontSize: 32, color: '#cbd5e1', mb: 1 }} />
                <Typography sx={{ fontSize: '0.82rem' }}>No consumables match.</Typography>
              </Box>
            ) : (
              filteredItems.map((item) => {
                const onHand = item.stock_qty || 0;
                const lowStock = onHand <= (item.reorder_level || 0);
                const outOfStock = onHand <= 0;
                const alreadyInCart = inCart(item.id);
                return (
                  <Box key={item.id} sx={{
                    px: 1.5, py: 1,
                    borderBottom: `1px solid #f1f5f9`,
                    bgcolor: alreadyInCart ? 'rgba(65,163,134,0.06)' : 'transparent',
                  }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG }} noWrap>
                          {item.name}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mt: 0.1 }}>
                          {item.category && (
                            <Typography sx={{ fontSize: '0.68rem', color: MUTED }}>{item.category}</Typography>
                          )}
                          <Typography sx={{ fontSize: '0.68rem', color: outOfStock ? '#991B1B' : (lowStock ? '#92400E' : '#15803D'), fontWeight: 700 }}>
                            {onHand} {item.base_unit}{onHand === 1 ? '' : 's'} on hand
                          </Typography>
                        </Stack>
                      </Box>
                      {alreadyInCart && (
                        <Chip size="small" icon={<CheckCircle sx={{ fontSize: 12 }} />} label="In cart"
                          sx={{ height: 20, fontSize: '0.66rem', fontWeight: 700, bgcolor: 'rgba(65,163,134,0.12)', color: '#15803D' }} />
                      )}
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <TextField size="small" type="number" placeholder={`Qty ${item.base_unit}s`}
                        value={quickQty[item.id] ?? ''}
                        onChange={(e) => setQuickQty(prev => ({ ...prev, [item.id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') addToCart(item); }}
                        inputProps={{ min: 1, max: onHand }}
                        disabled={outOfStock}
                        sx={{ width: 140 }} />
                      <Button size="small" variant="contained" disableElevation
                        startIcon={<Add sx={{ fontSize: 14 }} />}
                        onClick={() => addToCart(item)}
                        disabled={outOfStock || !quickQty[item.id]}
                        sx={{
                          textTransform: 'none', fontSize: '0.72rem', fontWeight: 700,
                          bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK },
                          px: 1.25, py: 0.4, minWidth: 0,
                        }}>
                        {alreadyInCart ? 'Update' : 'Add'}
                      </Button>
                    </Stack>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>

        {/* RIGHT — cart */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 1.5, borderBottom: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <ShoppingCart sx={{ color: PRIMARY }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>Requisition cart</Typography>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: STRONG }}>
                  {cart.length} item{cart.length === 1 ? '' : 's'} · {totalUnits} unit{totalUnits === 1 ? '' : 's'}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.78rem', color: MUTED }}>
                Submitted by: <strong style={{ color: STRONG }}>{userInfo?.name || userInfo?.username || '—'}</strong>
              </Typography>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, bgcolor: '#f8fafc' }}>
            {cart.length === 0 ? (
              <Box sx={{ p: 6, textAlign: 'center', color: MUTED }}>
                <ShoppingCart sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
                <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: STRONG }}>
                  Cart is empty
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', mt: 0.5 }}>
                  Pick consumables on the left, enter a quantity, and click Add.
                </Typography>
              </Box>
            ) : (
              <Paper variant="outlined" sx={{ borderColor: BORDER }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...LABEL_SX, bgcolor: '#fff' }}>Item</TableCell>
                        <TableCell sx={{ ...LABEL_SX, bgcolor: '#fff' }}>On hand</TableCell>
                        <TableCell sx={{ ...LABEL_SX, bgcolor: '#fff' }}>Requested qty</TableCell>
                        <TableCell sx={{ ...LABEL_SX, bgcolor: '#fff' }}>Remarks</TableCell>
                        <TableCell sx={{ ...LABEL_SX, bgcolor: '#fff' }} align="right"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cart.map((c) => {
                        const tooMuch = (parseInt(c.quantity, 10) || 0) > c.stock_qty;
                        return (
                          <TableRow key={c.consumableId} hover>
                            <TableCell sx={{ py: 1.25 }}>
                              <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG }}>
                                {c.name}
                              </Typography>
                              <Typography sx={{ fontSize: '0.7rem', color: MUTED }}>
                                Unit: {c.base_unit}
                                {c.pack_size > 1 && ` · ${c.pack_size}/${(c.pack_label || 'pack').toLowerCase()}`}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.25, fontSize: '0.82rem', fontWeight: 700, color: STRONG, fontVariantNumeric: 'tabular-nums' }}>
                              {c.stock_qty}
                            </TableCell>
                            <TableCell sx={{ py: 1.25 }}>
                              <TextField size="small" type="number"
                                value={c.quantity}
                                onChange={(e) => updateLine(c.consumableId, 'quantity', e.target.value)}
                                error={tooMuch}
                                helperText={tooMuch ? `Max ${c.stock_qty}` : ''}
                                inputProps={{ min: 1, max: c.stock_qty }}
                                sx={{ width: 130 }} />
                            </TableCell>
                            <TableCell sx={{ py: 1.25 }}>
                              <TextField size="small" placeholder="e.g. for OPD restock"
                                value={c.remarks}
                                onChange={(e) => updateLine(c.consumableId, 'remarks', e.target.value)}
                                sx={{ width: '100%', minWidth: 180 }} />
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.25 }}>
                              <IconButton size="small" onClick={() => removeLine(c.consumableId)} sx={{ color: '#b91c1c' }}>
                                <DeleteOutline sx={{ fontSize: 18 }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, bgcolor: '#fff', borderTop: `1px solid ${BORDER}` }}>
        <Typography sx={{ fontSize: '0.78rem', color: MUTED, mr: 'auto' }}>
          {cart.length === 0
            ? 'Add items to the cart before submitting.'
            : <>Ready — <strong style={{ color: STRONG }}>{cart.length}</strong> line{cart.length === 1 ? '' : 's'} totalling <strong style={{ color: STRONG }}>{totalUnits}</strong> unit{totalUnits === 1 ? '' : 's'}.</>}
        </Typography>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none', color: MUTED }}>
          Cancel
        </Button>
        <Button
          variant="contained" disableElevation
          startIcon={submitting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Send sx={{ fontSize: 16 }} />}
          onClick={handleSubmit}
          disabled={submitting || cart.length === 0}
          sx={{ textTransform: 'none', fontWeight: 700, bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK } }}>
          {submitting ? 'Submitting…' : 'Send to Procurement'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConsumableRequisitionDialog;
